import type { FC, ReactNode } from 'react';
import { Icon, type IconName } from '../assets/icons';
import { cn } from '../lib/cn';
import { Loader, TooltipElement, Typography } from '../ui';
import { LineChart, type LineChartProps } from '../ui/charts/line-chart';
import { Indicator, type IndicatorProps } from '../ui/indicator';

type FocusSummaryCardProps = {
  icon: IconName;
  title: string;
  value?: string;
  indicator?: Pick<
    IndicatorProps,
    'value' | 'negativeValue' | 'className' | 'negativeAsPositive'
  >;
  chart?: LineChartProps;
  loading?: boolean;
  showChart?: boolean;
  tooltipMessage?: string;
  valueAsComment?: boolean;
  headerCollapsed?: boolean;
  topRightAddon?: ReactNode;
  className?: string;
};

export const FocusSummaryCard: FC<FocusSummaryCardProps> = ({
  icon,
  title,
  value,
  indicator,
  chart,
  loading = true,
  showChart = false,
  tooltipMessage,
  valueAsComment = false,
  headerCollapsed = false,
  topRightAddon,
  className,
}) => {
  if (headerCollapsed) {
    return (
      <div className="bg-neutral-1 border-neutral-4 @container flex w-full min-w-0 flex-col gap-1 border p-3.5">
        <div className="flex items-center gap-2">
          <Icon className="text-neutral-7" size={16} name={icon} />
          <Typography noWrap className="text-neutral-8">
            {title}
          </Typography>
          {tooltipMessage && <TooltipElement content={tooltipMessage} />}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="flex items-center justify-between leading-none">
            <div className="flex items-center gap-2">
              <Typography
                title={value}
                noWrap={!valueAsComment}
                as={valueAsComment ? 'p' : 'h3'}
                variant={valueAsComment ? 'body' : 'h3'}
                weight={valueAsComment ? 'regular' : 'medium'}
                className={cn(
                  'text-neutral-12',
                  valueAsComment && 'text-neutral-8 h-8'
                )}
              >
                {value}
              </Typography>
              {indicator && <Indicator {...indicator} />}
            </div>

            {chart && (
              <div
                className={cn(
                  // Card-width gated — see the note on the other variant below.
                  'h-[54px] max-w-1/2 min-w-0 flex-1 items-center',
                  showChart ? 'hidden @[260px]:flex' : 'hidden @[300px]:flex'
                )}
              >
                <LineChart {...chart} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-neutral-1 border-neutral-4 @container relative flex w-full min-w-0 items-center justify-between gap-2 border p-3.5',
        className
      )}
    >
      {topRightAddon && (
        <div className="absolute top-3.5 right-3.5">{topRightAddon}</div>
      )}
      <div className="flex flex-col gap-1">
        <Icon className="text-neutral-7" size={16} name={icon} />
        <div className="flex items-center gap-2">
          <Typography noWrap className="text-neutral-8">
            {title}
          </Typography>
          {tooltipMessage && <TooltipElement content={tooltipMessage} />}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            <Typography
              title={value}
              noWrap={!valueAsComment}
              as={valueAsComment ? 'p' : 'h3'}
              variant={valueAsComment ? 'body' : 'h3'}
              weight={valueAsComment ? 'regular' : 'medium'}
              className={cn(
                'text-neutral-12',
                valueAsComment && 'text-neutral-8 h-8'
              )}
            >
              {value}
            </Typography>
            {indicator && <Indicator {...indicator} />}
          </div>
        )}
      </div>
      {chart && !loading && (
        <div
          className={cn(
            // Gated on the CARD's width, not the viewport's. `xl:` kept the
            // sparkline visible whenever the window was wide, so once the AI
            // Insight panel squeezed the column the chart collapsed to a
            // useless 30px sliver. Figma drops the sparkline entirely at that
            // card width (node 139-56998).
            //
            // `showChart` still means "this card wants its sparkline" — it just
            // gets a lower bar rather than an unconditional one, because below
            // ~260px there is nothing legible left to draw.
            'h-[78px] max-w-[50%] min-w-0 flex-1 items-center',
            showChart ? 'hidden @[260px]:flex' : 'hidden @[300px]:flex'
          )}
        >
          <LineChart {...chart} />
        </div>
      )}
    </div>
  );
};
